import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal, type Signal } from '@angular/core';
import type { Link } from '@readium/shared';
import { Layout, Manifest, Profile } from '@readium/shared';
import { debugLog } from '../../helpers/debugLog';
import { type ComponentNames, extractFileNameWithoutExtension } from '../../helpers/locator';
import { deserializeUserSettings } from '../../helpers/settings';
import type { MetadataResponse } from '../../models/metadata-response';
import type { MetadataInfo } from '../../models/text-info';
import { OfflineBookRegistry } from '../offline/offline-db';
import {
  isClockRolledBack,
  isLoanExpired,
  isOpenedFromExtension,
  loanKeyOf,
} from '../offline/offline-keys';
import { OfflineSessionService } from '../offline/offline-session';
import { OfflineSyncService } from '../offline/offline-sync';
import { snapshotOf, withLatestConfig, withSnapshot } from '../offline/offline-writes';
import { ReaderApi } from '../reader-api/reader-api';
import { ViewportService } from '../viewport/viewport';

export interface ChapterTOC {
  link: Link;
  level?: number;
}

@Injectable({
  providedIn: 'root',
})
export class MetadataService {
  private static readonly PRE_PAGINATED = 'pre-paginated';
  private static readonly AI_PERMISSION = 'enableAi';
  private static readonly DEFAULT_VOICE_RECORDING_MAX_SECONDS = 30;
  private static readonly MANAGE_QUESTIONS_PERMISSION = 'manageQuestions';
  /** The server's own default when its config doesn't set MIN_CHARS_TO_SEARCH. */
  private static readonly DEFAULT_MIN_CHARS_TO_SEARCH = 4;

  private readonly readerApi = inject(ReaderApi);
  private readonly viewportService = inject(ViewportService);
  private readonly offlineRegistry = inject(OfflineBookRegistry);
  private readonly offlineSession = inject(OfflineSessionService);
  private readonly offlineSync = inject(OfflineSyncService);

  /** Response as received (manifest not deserialized), what offline reading stores. */
  private rawMetadata?: MetadataResponse;

  readonly metadata = signal<MetadataResponse | undefined>(undefined);
  /** Token the reader was opened with (the one in the URL, or the cookie on a reload). */
  readonly openToken = signal('');
  readonly isUnauthorized = signal(false);

  readonly resourceTitle = computed(() => this.metadata()?.readium2.manifest.metadata.title);

  readonly positions = computed(() => this.metadata()?.readium2.positions.positions ?? []);

  /** File name of each spine item by its OPF idref, the chapter id the previous NubeReader saved. */
  readonly componentNames = computed<ComponentNames>(
    () =>
      new Map(
        (this.metadata()?.readium.spine.items ?? []).map((item) => [
          item.idref,
          extractFileNameWithoutExtension(item.href),
        ]),
      ),
  );

  readonly chapters = computed<ChapterTOC[]>(
    () =>
      this.metadata()?.readium2.manifest.toc?.items.flatMap((item) =>
        item.children
          ? [{ link: item }, ...item.children.items.map((i) => ({ link: i, level: 1 }))]
          : [{ link: item }],
      ) ?? [],
  );

  readonly userLanguage = computed(() => this.metadata()?.config.userLanguage ?? 'en');
  /** Shortest text the server accepts in a search (it answers 400 below this). */
  readonly minCharsToSearch = computed<number>(
    () =>
      Number(this.metadata()?.config.minCharsToSearch) ||
      MetadataService.DEFAULT_MIN_CHARS_TO_SEARCH,
  );

  readonly isFixedLayout = computed(
    () => this.metadata()?.readium.rendition_layout === MetadataService.PRE_PAGINATED || false,
  );

  // Books with their own recorded narration (EPUB Media Overlays): SMIL files linked from the spine
  readonly hasMediaOverlay = computed(
    () =>
      this.metadata()?.readium.spine.items.some((item) => Boolean(item.media_overlay_id)) ?? false,
  );

  readonly isTeacher = computed(() => this.metadata()?.userConfig?.readMode === 'teacher');

  readonly canManageQuestions = computed(
    () =>
      this.isTeacher() &&
      Boolean(
        this.metadata()?.userConfig?.userPermissions?.includes(
          MetadataService.MANAGE_QUESTIONS_PERMISSION,
        ),
      ),
  );

  readonly isUserFromBookClub = computed(() => this.metadata()?.userConfig?.userFromBookclub);

  readonly sharedSelection = computed(() => this.metadata()?.sharedSelection);

  readonly canUseAi = computed(() => {
    const metadata = this.metadata();
    return Boolean(
      !this.offlineSession.isOffline() &&
        metadata?.metadata.readerConfig.enableAi &&
        metadata.userConfig?.userPermissions?.includes(MetadataService.AI_PERMISSION),
    );
  });

  // Like the previous NubeReader, multiple attempts are only offered when the reader enables them.
  readonly canAddAttempts = computed(() =>
    Boolean(this.metadata()?.metadata.readerConfig.enableAttempts),
  );

  // Students must answer the questions on a page before moving on, as in the previous NubeReader.
  readonly restrictNextPageWithQuestions = computed(
    () =>
      Boolean(this.metadata()?.metadata.readerConfig.restrictNextPageWithQuestions) &&
      !this.isTeacher(),
  );

  readonly competencies = computed(() => this.metadata()?.userConfig?.competencies ?? []);

  /** Also offline: like the other questions, it is created on the server once synced. */
  readonly canCreateVoiceQuestions = computed(() =>
    Boolean(this.metadata()?.metadata.readerConfig.enableVoiceQuestions),
  );

  readonly voiceRecordingMaxSeconds = computed(
    () =>
      this.metadata()?.metadata.readerConfig.voiceRecordingMaxSeconds ??
      MetadataService.DEFAULT_VOICE_RECORDING_MAX_SECONDS,
  );

  /** Whether Azure can assess, and read aloud, the language of the book. */
  readonly isPronunciationLanguage = computed(() => {
    const readerConfig = this.metadata()?.metadata.readerConfig;
    const language = this.metadata()?.metadata.language.split('-')[0];
    return Boolean(readerConfig && language && language in readerConfig.pronunciationLocales);
  });

  readonly canUsePronunciationAssessment = computed(
    () =>
      Boolean(this.metadata()?.metadata.readerConfig.enablePronunciationAssessment) &&
      this.isPronunciationLanguage(),
  );

  readonly metadataInfo: Signal<MetadataInfo> = computed(() => ({
    userId: this.metadata()?.userData.userId,
    clientId: this.metadata()?.otkId,
    resourceLanguage: this.metadata()?.metadata.language,
    userLanguage: this.metadata()?.config.userLanguage,
    title: this.metadata()?.metadata.title,
  }));

  readonly otkConfig = computed(() => ({
    otkId: this.metadata()?.otkId,
    otkDomain: this.metadata()?.otkDomain,
    bookId: this.metadata()?.bookId,
    userId: this.metadata()?.userData.userId,
  }));

  readonly userSettings = computed(() =>
    this.metadata()
      ? deserializeUserSettings(this.metadata()!.userData.settings, this.viewportService)
      : undefined,
  );

  readonly xBookId = computed(
    () => `${this.metadata()?.otkId}${this.metadata()?.bookId}${this.metadata()?.config.mode}`,
  );

  readonly isPreviewMode = computed(() => this.metadata()?.config.mode === 'preview');
  /** Set for shared-highlight sessions: the reader position must not be saved. */
  readonly isReadProgressDisabled = computed(() => Boolean(this.metadata()?.disableReadProgress));

  readonly isOfflineModeEnabled = computed(() => {
    const metadata = this.metadata();
    return Boolean(metadata?.config.offlineMode) && metadata?.config.mode !== 'preview';
  });

  readonly loanKey = computed(() => {
    const metadata = this.metadata();
    return metadata ? loanKeyOf(metadata) : undefined;
  });

  readonly checkoutId = computed(() => this.metadata()?.config.checkoutId);

  constructor() {
    effect(() => {
      cookieStore.set('x-bookid', this.xBookId());
    });
  }

  getRawMetadata(): MetadataResponse | undefined {
    return this.rawMetadata;
  }

  async loadMetadata(token: string): Promise<void> {
    this.openToken.set(token);
    if (!token) {
      this.isUnauthorized.set(true);
      return;
    }
    // Always ask the API first: `navigator.onLine` can be false with a working connection (some
    // Linux networks, VPNs), and without network the request fails at once anyway.
    try {
      const fetched = await this.readerApi.getMetadataResource(token);
      const metadataResponse = await this.prepareDownloadedCopy(token, fetched);
      this.offlineSession.startOnline();
      this.applyMetadata(metadataResponse);
    } catch (error) {
      if (!(error instanceof HttpErrorResponse)) throw error;
      if ((error.status as HttpStatusCode) === HttpStatusCode.Unauthorized) {
        if (isOpenedFromExtension(location.hash)) this.offlineSession.block('offlineOnlyLink');
        else this.isUnauthorized.set(true);
      } else if (
        MetadataService.isUnreachable(error) &&
        (!navigator.onLine || (await this.hasOfflineCopy(token)))
      ) {
        // With a network "up" the API may just be down: only a downloaded book opens offline then.
        await this.loadOfflineMetadata(token);
      } else {
        throw error;
      }
    }
  }

  /**
   * Chapter id to save for `href`, the way the previous NubeReader saved it (its OPF idref) so
   * both readers place what the other saved; its file name when the spine does not list it.
   */
  componentIdFor(href: string): string {
    const name = extractFileNameWithoutExtension(href);
    for (const [idref, fileName] of this.componentNames()) {
      if (fileName === name) return idref;
    }
    return name;
  }

  /** No network (status 0), or the service worker could not reach the server (504). */
  private static isUnreachable(error: HttpErrorResponse): boolean {
    return error.status === 0 || (error.status as HttpStatusCode) === HttpStatusCode.GatewayTimeout;
  }

  private async hasOfflineCopy(token: string): Promise<boolean> {
    const book = await this.offlineRegistry.findByToken(token).catch(() => undefined);
    return book?.status === 'complete';
  }

  private applyMetadata(metadataResponse: MetadataResponse): void {
    this.rawMetadata = structuredClone(metadataResponse);
    const manifest = Manifest.deserialize(metadataResponse.readium2.manifest);
    if (!manifest) return;
    if (metadataResponse.readium.rendition_layout === MetadataService.PRE_PAGINATED) {
      manifest.metadata.layout = Layout.fixed;
      if (!manifest.metadata.conformsTo?.includes(Profile.EPUB)) {
        manifest.metadata.conformsTo = [...(manifest.metadata.conformsTo ?? []), Profile.EPUB];
      }
    }

    metadataResponse.readium2.manifest = manifest;
    this.metadata.set(metadataResponse);
  }

  private async loadOfflineMetadata(token: string): Promise<void> {
    const book = await this.offlineRegistry.findByToken(token).catch(() => undefined);
    if (book?.status !== 'complete') {
      this.offlineSession.block('notDownloaded');
      return;
    }
    const now = Date.now();
    if (isClockRolledBack(book, now)) {
      this.offlineSession.block('clockRolledBack');
      return;
    }
    if (isLoanExpired(book, now)) {
      this.offlineSession.block('expired');
      return;
    }
    await this.offlineRegistry.update(book.loanKey, { lastSeenAt: now });
    const snapshot =
      (await this.offlineRegistry.getSnapshot(book.loanKey)) ??
      snapshotOf(book.loanKey, book.metadata, []);
    this.offlineSession.startOffline(book, snapshot);
    this.applyMetadata(structuredClone(withSnapshot(book.metadata, snapshot)));
  }

  /**
   * When the book is downloaded in this browser: sends the changes made offline (and reloads the
   * metadata so the reader shows them), lets the copy be found offline from this token, and
   * refreshes the copy with the server data and configuration.
   */
  private async prepareDownloadedCopy(
    token: string,
    fetched: MetadataResponse,
  ): Promise<MetadataResponse> {
    try {
      const loanKey = loanKeyOf(fetched);
      const stored = await this.offlineRegistry.get(loanKey);
      if (stored?.status !== 'complete') return fetched;
      this.offlineSession.setDownloadedLoan(loanKey);
      let latest = fetched;
      if (await this.offlineSync.pendingCount(loanKey)) {
        const { sent } = await this.offlineSync.flush(loanKey, fetched.token);
        if (sent)
          latest = await this.readerApi.getMetadataResource(fetched.token).catch(() => fetched);
      }
      await this.offlineRegistry.addTokens(loanKey, [token, fetched.token, latest.token]);
      await this.offlineRegistry.update(loanKey, {
        lastSeenAt: Date.now(),
        metadata: withLatestConfig(stored.metadata, latest),
      });
      // With changes still queued, the copy keeps them instead of the server data.
      if (!(await this.offlineSync.pendingCount(loanKey))) {
        const previous = await this.offlineRegistry.getSnapshot(loanKey);
        await this.offlineRegistry.putSnapshot(
          snapshotOf(loanKey, latest, previous?.selections ?? []),
        );
      }
      return latest;
    } catch (error) {
      debugLog('Offline copy not updated', error);
      return fetched;
    }
  }
}
